import React from 'react';
import { MapPin, Trash2, Bell } from 'lucide-react';
import { Registration, deleteAlert } from '../services/alerts';
import { DeleteAlertModal } from './DeleteAlertModal';

interface AlertsListProps {
  registrations: Registration[];
  isLoading: boolean;
  onDeleteSuccess?: () => void;
}

export function AlertsList({ registrations, isLoading, onDeleteSuccess }: AlertsListProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [alertToDelete, setAlertToDelete] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async (alertId: string) => {
    try {
      setIsDeleting(true);
      setError(null);
      await deleteAlert(alertId);
      
      // Notificar o componente pai sobre a exclusão bem-sucedida
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir alerta');
    } finally {
      setIsDeleting(false);
      setAlertToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Carregando alertas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-lg">
        <p className="font-medium">{error}</p>
      </div>
    );
  }

  if (registrations.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <div className="mb-4">
          <Bell className="mx-auto text-gray-400" size={48} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Nenhum alerta cadastrado
        </h3>
        <p className="text-gray-600">
          Cadastre seu primeiro alerta para começar a receber ofertas de passagens aéreas.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {registrations.map((registration) => (
          <div
            key={registration.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >            
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin size={16} />
                <span>De: {registration.origin_city}</span>
              </div>
              
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Para:</div>
                <div className="flex flex-wrap gap-2">
                  {registration.destination_cities.map(city => (
                    <span
                      key={city.id}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      <MapPin size={14} className="text-gray-400" />
                      {city.city_name}
                    </span>
                  ))}
                </div>
              </div>

              {registration.max_price && (
                <div className="text-sm text-gray-600">
                  Valor máximo: R$ {registration.max_price.toFixed(2)}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setAlertToDelete(registration.id)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                  title="Excluir alerta"
                  disabled={isDeleting}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alertToDelete && (
        <DeleteAlertModal
          onConfirm={() => handleDelete(alertToDelete)}
          onCancel={() => setAlertToDelete(null)}
        />
      )}
    </>
  );
}